import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCity } from "../context/CityContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  CheckCircle2, ShieldCheck, Handshake, Sparkles, ArrowRight, ArrowLeft,
  Phone, Search, Car, Bike, Truck, Clock, MapPin, IndianRupee, Info, BadgeCheck,
} from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";
import KycUpload from "../components/KycUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Verify", "About", "Skills", "Work", "Vehicle"];
const LANGUAGES = ["Hindi", "English", "Marathi", "Tamil", "Telugu", "Bengali", "Kannada", "Gujarati", "Punjabi"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const AVAIL_TIMES = ["Morning", "Afternoon", "Evening", "Night", "Anytime"];
const VEHICLE_TYPES = ["motorcycle", "scooter", "car", "auto", "commercial", "other"];
const VEHICLE_LABELS = { motorcycle: "Motorcycle", scooter: "Scooter", car: "Car", auto: "Auto", commercial: "Commercial", other: "Other" };
const DL_TYPES = ["MCWG", "LMV", "HMV", "Transport", "Other"];

function Stepper({ step, total }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-10" : "w-4"}`}
          style={{ background: i <= step ? "var(--secondary)" : "rgba(0,0,0,0.15)" }} />
      ))}
    </div>
  );
}

function EarnBanner() {
  return (
    <div className="rounded-3xl overflow-hidden mb-6 relative"
      style={{ background: "linear-gradient(135deg, var(--secondary), #ff8f52)" }}>
      <div className="p-5 md:p-7 text-white relative">
        <div className="flex items-center gap-2">
          <IndianRupee className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/90">flexible earning</span>
        </div>
        <div className="font-display text-3xl md:text-4xl mt-1 leading-tight">
          Earn up to <span className="italic">₹30,000/month*</span>
        </div>
        <div className="text-sm mt-1 text-white/90">Work from anywhere. Choose when and where you want to help.</div>
        <div className="mt-3 text-[11px] text-white/80 flex gap-1.5 items-start">
          <Info className="w-3 h-3 mt-0.5 shrink-0" />
          <span>*Earnings vary based on services, availability, location and completed work.</span>
        </div>
      </div>
    </div>
  );
}

function VerifyStep({ form, setForm, onVerified }) {
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [otp, setOtp] = useState("");

  const send = async () => {
    if (!/^\+?\d{6,15}$/.test(form.phone || "")) { toast.error("Enter a valid mobile number"); return; }
    if (form.aadhaar_last4 && !/^\d{4}$/.test(form.aadhaar_last4)) { toast.error("Aadhaar last 4 must be 4 digits"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/verify/send-otp`, { phone: form.phone, aadhaar_last4: form.aadhaar_last4 || null });
      setOtpSent(true);
      if (r.data.dev_otp) setDevOtp(r.data.dev_otp);
      toast.success("OTP sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (otp.length < 4) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/verify/confirm-otp`, { phone: form.phone, otp });
      toast.success("Mobile verified");
      onVerified();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong OTP");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm flex gap-3 text-emerald-900">
        <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
        <div>Quick mobile verification — just like Swiggy or Zomato riders. Only the last 4 digits of Aadhaar are stored.</div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Mobile number</Label>
        <div className="mt-1.5 relative">
          <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="verify-phone" className="pl-9" placeholder="+91 98765 43210" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.trim() })} />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Aadhaar — last 4 digits <span className="text-neutral-400 normal-case">(optional)</span></Label>
        <Input data-testid="verify-aadhaar4" className="mt-1.5 tracking-widest" maxLength={4} placeholder="1234"
          value={form.aadhaar_last4} onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} />
        <p className="text-[11px] text-neutral-500 mt-1">Full Aadhaar is never asked or stored — only last 4 for record-keeping.</p>
      </div>

      {!otpSent ? (
        <button data-testid="verify-send-otp-btn" onClick={send} disabled={busy} className="btn-primary w-full">
          {busy ? "Sending…" : "Send OTP"}
        </button>
      ) : (
        <div className="space-y-3">
          {devOtp && (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5" />
              <span>Dev mode — your OTP is <b className="font-mono" data-testid="dev-otp-value">{devOtp}</b>. Wire an SMS provider in production.</span>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Enter OTP</Label>
            <Input data-testid="verify-otp-input" className="mt-1.5 tracking-[0.5em] text-center text-lg" maxLength={6}
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          </div>
          <div className="flex gap-2">
            <button data-testid="verify-resend-btn" onClick={send} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-full border border-black/15 bg-white text-sm font-bold">
              Resend
            </button>
            <button data-testid="verify-confirm-btn" onClick={confirm} disabled={busy || otp.length < 4}
              className="btn-primary flex-1">
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AboutStep({ form, setForm, cities, user, token }) {
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Profile photo</Label>
        <div className="mt-3">
          <PhotoUpload value={user?.photo_path} token={token} onUpload={() => {}} />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">City</Label>
        <Select value={form.city_slug} onValueChange={(v) => setForm({ ...form, city_slug: v })}>
          <SelectTrigger data-testid="mate-city-select" className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cities.map((c) => (
              <SelectItem key={c.slug} value={c.slug}>{c.name} {c.status === "live" ? "· Live" : "· Soon"}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Short bio</Label>
        <Textarea data-testid="mate-bio" rows={4} className="mt-1.5"
          placeholder="Tell customers who you are, your strengths, and why they can trust you (min 10 chars)."
          value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Experience (years)</Label>
        <Input data-testid="mate-experience" type="number" min="0" max="60" className="mt-1.5"
          value={form.experience_years}
          onChange={(e) => setForm({ ...form, experience_years: Number(e.target.value || 0) })} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Languages you speak</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {LANGUAGES.map((l) => {
            const active = form.languages.includes(l);
            return (
              <button key={l} type="button" data-testid={`mate-lang-${l.toLowerCase()}`}
                onClick={() => setForm({ ...form, languages: active ? form.languages.filter((x) => x !== l) : [...form.languages, l] })}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${active ? "text-white" : "text-neutral-700 bg-white"}`}
                style={{ borderColor: active ? "var(--secondary)" : "rgba(0,0,0,0.15)", background: active ? "var(--secondary)" : "#fff" }}>
                {l}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SkillsStep({ form, setForm, skills }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return skills;
    return skills.filter((sk) => sk.name.toLowerCase().includes(s));
  }, [q, skills]);
  const toggle = (slug) => setForm({
    ...form,
    skills: form.skills.includes(slug) ? form.skills.filter((s) => s !== slug) : [...form.skills, slug],
  });
  return (
    <div className="space-y-5">
      <EarnBanner />
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">What can you help with?</Label>
        <div className="mt-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="skill-search" className="pl-9" placeholder="Search skills — e.g. cleaning, tuition, driver"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">
          {form.skills.length} selected · pick as many as you like
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
          {filtered.map((s) => {
            const active = form.skills.includes(s.slug);
            return (
              <button key={s.slug} type="button" data-testid={`skill-${s.slug}`} onClick={() => toggle(s.slug)}
                className={`flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all ${
                  active ? "border-[color:var(--secondary)] bg-orange-50" : "border-black/10 bg-white hover:border-black/20"
                }`}>
                <div className={`w-6 h-6 rounded-full grid place-items-center text-xs shrink-0 ${active ? "text-white" : "text-neutral-400 border border-black/10"}`}
                  style={active ? { background: "var(--secondary)" } : {}}>
                  {active ? "✓" : ""}
                </div>
                <span className="font-semibold text-sm">{s.name}</span>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="col-span-2 text-neutral-500 text-sm py-4">No matches. Try a different word.</div>}
        </div>
      </div>
      {form.skills.includes("other") && (
        <div>
          <Label className="text-xs uppercase tracking-widest text-neutral-500">Your own skill</Label>
          <Input data-testid="skill-custom" className="mt-1.5" placeholder="e.g. Piano tuning, plant care…"
            value={form.custom_skill || ""} onChange={(e) => setForm({ ...form, custom_skill: e.target.value })} />
        </div>
      )}
    </div>
  );
}

function WorkStep({ form, setForm, cities }) {
  const toggleDay = (d) => setForm({
    ...form,
    available_days: form.available_days.includes(d) ? form.available_days.filter((x) => x !== d) : [...form.available_days, d],
  });
  return (
    <div className="space-y-5">
      <EarnBanner />
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Preferred city</Label>
        <Select value={form.primary_city || form.city_slug} onValueChange={(v) => setForm({ ...form, primary_city: v })}>
          <SelectTrigger data-testid="mate-primary-city" className="mt-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cities.map((c) => (<SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center justify-between">
          <span>Areas / localities you can serve</span>
          <span className="text-neutral-400 normal-case">{form.service_areas.length} added</span>
        </Label>
        <div className="mt-1.5 flex gap-2 flex-wrap">
          {form.service_areas.map((a, i) => (
            <span key={i} className="chip">
              <MapPin className="w-3 h-3" /> {a}
              <button onClick={() => setForm({ ...form, service_areas: form.service_areas.filter((_, j) => j !== i) })} className="ml-1 text-neutral-500 hover:text-red-500">×</button>
            </span>
          ))}
          <input data-testid="mate-service-areas-input" placeholder="Type area & press Enter"
            className="flex-1 min-w-[180px] rounded-full border border-black/15 px-3 py-1.5 text-sm bg-white"
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                setForm({ ...form, service_areas: [...form.service_areas, e.target.value.trim()] });
                e.target.value = "";
              }
            }} />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Work from anywhere?</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          {[true, false].map((v) => (
            <button key={String(v)} type="button" data-testid={`mate-wfa-${v}`}
              onClick={() => setForm({ ...form, work_from_anywhere: v })}
              className={`rounded-xl border-2 px-4 py-3 text-sm font-bold transition-all ${form.work_from_anywhere === v ? "border-[color:var(--secondary)] bg-orange-50" : "border-black/10 bg-white"}`}>
              {v ? "Yes — anywhere in city" : "No — only my areas"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Type of work</Label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {["fulltime", "parttime", "flexible"].map((t) => (
            <button key={t} type="button" data-testid={`mate-availtype-${t}`}
              onClick={() => setForm({ ...form, availability_type: t })}
              className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold capitalize transition-all ${form.availability_type === t ? "border-[color:var(--secondary)] bg-orange-50" : "border-black/10 bg-white"}`}>
              {t.replace("time", "-time")}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Available days</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {DAYS.map((d) => {
            const active = form.available_days.includes(d);
            return (
              <button key={d} type="button" data-testid={`mate-day-${d.toLowerCase()}`} onClick={() => toggleDay(d)}
                className="px-3 py-1.5 rounded-full border text-xs font-semibold"
                style={{ borderColor: active ? "var(--secondary)" : "rgba(0,0,0,0.15)", background: active ? "var(--secondary)" : "#fff", color: active ? "#fff" : "#333" }}>
                {d}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Available time</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {AVAIL_TIMES.map((t) => {
            const active = (form.available_time || "").toLowerCase() === t.toLowerCase();
            return (
              <button key={t} type="button" data-testid={`mate-time-${t.toLowerCase()}`}
                onClick={() => setForm({ ...form, available_time: t.toLowerCase() })}
                className="px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1"
                style={{ borderColor: active ? "var(--secondary)" : "rgba(0,0,0,0.15)", background: active ? "var(--secondary)" : "#fff", color: active ? "#fff" : "#333" }}>
                <Clock className="w-3 h-3" /> {t}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Minimum expected earning / month (₹)</Label>
        <div className="mt-1.5 relative">
          <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input data-testid="mate-min-earning" type="number" min="0" step="500" className="pl-9"
            value={form.min_expected_earning_inr}
            onChange={(e) => setForm({ ...form, min_expected_earning_inr: Number(e.target.value || 0) })} />
        </div>
      </div>
    </div>
  );
}

function VehicleStep({ form, setForm, token }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm flex gap-3 text-emerald-900">
        <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
        <div>Driving licence, RC and insurance documents are <b>private</b> — visible only to you and our admin verification team.</div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-500">Do you drive?</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          {[true, false].map((v) => (
            <button key={String(v)} type="button" data-testid={`mate-drives-${v}`}
              onClick={() => setForm({
                ...form, drives: v,
                ...(v ? {} : {
                  has_dl: false, dl_number: "", dl_type: "", dl_expiry: "",
                  dl_front_path: null, dl_back_path: null,
                  has_vehicle: false, vehicle_type: "", vehicle_registration: "",
                  rc_path: null, insurance_expiry: "", puc_expiry: "",
                }),
              })}
              className={`rounded-xl border-2 px-4 py-3 text-sm font-bold ${form.drives === v ? "border-[color:var(--secondary)] bg-orange-50" : "border-black/10 bg-white"}`}>
              {v ? "Yes, I drive" : "No"}
            </button>
          ))}
        </div>
      </div>

      {form.drives && (
        <>
          <div className="rounded-2xl border border-black/10 bg-neutral-50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-bold text-sm flex items-center gap-2"><BadgeCheck className="w-4 h-4" style={{ color: "var(--secondary)" }} /> Driving Licence</div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" data-testid="mate-has-dl" checked={form.has_dl}
                  onChange={(e) => setForm({ ...form, has_dl: e.target.checked })} />
                Yes, I have a DL
              </label>
            </div>
            {form.has_dl && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-neutral-500">Licence number</Label>
                    <Input data-testid="mate-dl-number" className="mt-1" value={form.dl_number || ""}
                      onChange={(e) => setForm({ ...form, dl_number: e.target.value.toUpperCase() })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-neutral-500">Category</Label>
                    <Select value={form.dl_type || ""} onValueChange={(v) => setForm({ ...form, dl_type: v })}>
                      <SelectTrigger data-testid="mate-dl-type" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{DL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-neutral-500">Expiry date</Label>
                  <Input data-testid="mate-dl-expiry" type="date" className="mt-1" value={form.dl_expiry || ""}
                    onChange={(e) => setForm({ ...form, dl_expiry: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <KycUpload kind="dl_front" label="DL — front" value={form.dl_front_path}
                    onChange={(p) => setForm({ ...form, dl_front_path: p })} token={token} testid="mate-dl-front" />
                  <KycUpload kind="dl_back" label="DL — back" value={form.dl_back_path}
                    onChange={(p) => setForm({ ...form, dl_back_path: p })} token={token} testid="mate-dl-back" />
                </div>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-black/10 bg-neutral-50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-bold text-sm flex items-center gap-2"><Car className="w-4 h-4" style={{ color: "var(--secondary)" }} /> Vehicle</div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" data-testid="mate-has-vehicle" checked={form.has_vehicle}
                  onChange={(e) => setForm({ ...form, has_vehicle: e.target.checked })} />
                Yes, I own one
              </label>
            </div>
            {form.has_vehicle && (
              <>
                <div>
                  <Label className="text-[10px] uppercase text-neutral-500">Vehicle type</Label>
                  <div className="mt-1.5 flex flex-wrap gap-2">
                    {VEHICLE_TYPES.map((v) => {
                      const active = form.vehicle_type === v;
                      const Ico = v === "motorcycle" ? Bike : v === "commercial" ? Truck : Car;
                      return (
                        <button key={v} type="button" data-testid={`mate-vehicle-${v}`}
                          onClick={() => setForm({ ...form, vehicle_type: v })}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${active ? "text-white" : "text-neutral-700 bg-white"}`}
                          style={{ borderColor: active ? "var(--secondary)" : "rgba(0,0,0,0.15)", background: active ? "var(--secondary)" : "#fff" }}>
                          <Ico className="w-3 h-3" /> {VEHICLE_LABELS[v]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-neutral-500">Registration number</Label>
                  <Input data-testid="mate-vehicle-reg" className="mt-1 tracking-widest"
                    placeholder="MH12 AB 1234"
                    value={form.vehicle_registration || ""}
                    onChange={(e) => setForm({ ...form, vehicle_registration: e.target.value.toUpperCase() })} />
                </div>
                <KycUpload kind="rc" label="Vehicle RC" value={form.rc_path}
                  onChange={(p) => setForm({ ...form, rc_path: p })} token={token} testid="mate-rc" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-neutral-500">Insurance expiry</Label>
                    <Input data-testid="mate-insurance-expiry" type="date" className="mt-1"
                      value={form.insurance_expiry || ""} onChange={(e) => setForm({ ...form, insurance_expiry: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-neutral-500">PUC expiry</Label>
                    <Input data-testid="mate-puc-expiry" type="date" className="mt-1"
                      value={form.puc_expiry || ""} onChange={(e) => setForm({ ...form, puc_expiry: e.target.value })} />
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatusChip({ label, status }) {
  const map = {
    verified: { c: "border-emerald-200 bg-emerald-50 text-emerald-700", i: BadgeCheck },
    pending: { c: "border-amber-200 bg-amber-50 text-amber-700", i: Clock },
    action_required: { c: "border-orange-200 bg-orange-50 text-orange-700", i: Info },
    rejected: { c: "border-red-200 bg-red-50 text-red-700", i: Info },
    not_applicable: { c: "border-neutral-200 bg-neutral-50 text-neutral-500", i: Info },
  };
  const s = map[status] || map.pending;
  const I = s.i;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${s.c}`}>
      <I className="w-3 h-3" /> {label}: {status.replace("_", " ")}
    </span>
  );
}

export default function BecomeMate() {
  const { user, token, authFetch } = useAuth();
  const { cities } = useCity();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    // Step 0 — verify
    phone: "", aadhaar_last4: "",
    // Step 1 — about
    city_slug: "pune", bio: "", experience_years: 0, languages: [],
    // Step 2 — skills
    skills: [], custom_skill: "",
    // Step 3 — work
    primary_city: "", service_areas: [], work_from_anywhere: true,
    availability_type: "flexible", available_days: [], available_time: "anytime",
    min_expected_earning_inr: 15000,
    // Step 4 — vehicle
    drives: false, has_dl: false, dl_number: "", dl_type: "", dl_expiry: "",
    dl_front_path: null, dl_back_path: null,
    has_vehicle: false, vehicle_type: "", vehicle_registration: "",
    rc_path: null, insurance_expiry: "", puc_expiry: "",
    // Legacy — kept for platform bookings routing
    services: [],
  });

  useEffect(() => {
    axios.get(`${API}/mate/skills`).then((r) => setSkills(r.data.skills || []));
    if (token) {
      authFetch({ method: "GET", url: `${API}/mate/profile` })
        .then((r) => setProfile(r.data.profile))
        .catch(() => {});
    }
  }, [token, authFetch]);

  useEffect(() => {
    if (user && !form.phone) setForm((f) => ({ ...f, phone: user.phone || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!token) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <Handshake className="w-10 h-10 mx-auto" style={{ color: "var(--primary)" }} />
        <h1 className="font-display text-4xl mt-4">Sign in to become a Mate</h1>
        <p className="text-neutral-600 mt-2">Create an account or sign in, then finish your Mate profile.</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/register" className="btn-primary">Create account</Link>
          <Link to="/login" className="px-6 py-3 rounded-full border-2 border-[color:var(--primary)] font-bold" style={{ color: "var(--primary)" }}>Sign in</Link>
        </div>
      </div>
    );
  }

  if (profile) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20">
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: "var(--secondary)" }} />
          <h1 className="font-display text-4xl mt-4">You're a Mate.</h1>
          <p className="text-neutral-600 mt-3 max-w-md mx-auto">
            Application <b>under review</b>. We'll verify your details and unlock jobs in <b>{profile.city_slug}</b> shortly.
          </p>
        </div>
        <div className="mt-8 rounded-3xl border border-black/10 bg-white p-6">
          <div className="label-eyebrow">verification</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <StatusChip label="Identity" status={profile.identity_verification || (profile.phone_verified ? "verified" : "pending")} />
            {profile.has_dl && <StatusChip label="Driving Licence" status={profile.dl_verification || "pending"} />}
            {profile.has_vehicle && <StatusChip label="Vehicle" status={profile.vehicle_verification || "pending"} />}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-neutral-500">City:</span> {profile.city_slug}</div>
            <div><span className="text-neutral-500">Experience:</span> {profile.experience_years} yrs</div>
            <div><span className="text-neutral-500">Phone:</span> {profile.phone || "—"}</div>
            <div><span className="text-neutral-500">Aadhaar (last 4):</span> {profile.aadhaar_last4 ? `••••${profile.aadhaar_last4}` : "—"}</div>
            <div className="sm:col-span-2"><span className="text-neutral-500">Skills:</span> {(profile.skills || []).map((s) => (skills.find((sk) => sk.slug === s)?.name || s)).join(", ") || "—"}</div>
            <div className="sm:col-span-2"><span className="text-neutral-500">Bio:</span> {profile.bio}</div>
          </div>
        </div>
        <div className="text-center mt-6">
          <Link to="/dashboard" className="btn-primary inline-block">Go to workspace</Link>
        </div>
      </div>
    );
  }

  const validate = () => {
    if (step === 0) return true; // verification handled internally
    if (step === 1) return form.city_slug && form.bio.length >= 10 && form.experience_years >= 0;
    if (step === 2) return form.skills.length >= 1;
    if (step === 3) return !!form.availability_type;
    if (step === 4) return true;
    return true;
  };

  const next = () => {
    if (!validate()) { toast.error("Please fill required fields"); return; }
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      // Map skills → legacy services so bookings/matching keep working.
      const skillToService = {
        "airport-assist": "airport", "railway-assist": "railway", "shopping-assist": "shopping",
        "delivery-assist": "document", "elderly-assist": "babyold", "child-assist": "babyold",
        "home-assist": "personal", "property-visit": "findflat", "property-deal": "findflat",
        "business-assist": "corporate", "document-collection": "document",
        "photography": "custom", "event-assist": "custom", "computer-online": "custom",
        "errands": "personal", "moving-shifting": "luggage", "tuition": "interview",
        "driver": "driver", "car-cleaning": "custom", "bike-cleaning": "custom", "other": "custom",
      };
      const services = Array.from(new Set(form.skills.map((s) => skillToService[s]).filter(Boolean)));
      await authFetch({
        method: "POST", url: `${API}/mate/onboard`,
        data: { ...form, services },
      });
      toast.success("Application submitted");
      const r = await authFetch({ method: "GET", url: `${API}/mate/profile` });
      setProfile(r.data.profile);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-24">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">become a mate</span>
        <Stepper step={step} total={STEPS.length} />
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-tight">
        {step === 0 && <>Verify your <span style={{ color: "var(--secondary)" }}>mobile</span>.</>}
        {step === 1 && <>Tell us about <span style={{ color: "var(--secondary)" }}>you</span>.</>}
        {step === 2 && <>What can you <span style={{ color: "var(--secondary)" }}>help</span> with?</>}
        {step === 3 && <>Where & when will you <span style={{ color: "var(--secondary)" }}>work</span>?</>}
        {step === 4 && <>Driving & <span style={{ color: "var(--secondary)" }}>vehicle</span> details.</>}
      </h1>
      <p className="text-sm text-neutral-500 mt-1">Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>

      <div className="mt-8 rounded-3xl border border-black/10 bg-white p-6 md:p-8">
        {step === 0 && <VerifyStep form={form} setForm={setForm} onVerified={() => setStep(1)} />}
        {step === 1 && <AboutStep form={form} setForm={setForm} cities={cities} user={user} token={token} />}
        {step === 2 && <SkillsStep form={form} setForm={setForm} skills={skills} />}
        {step === 3 && <WorkStep form={form} setForm={setForm} cities={cities} />}
        {step === 4 && <VehicleStep form={form} setForm={setForm} token={token} />}
      </div>

      {/* Navigation */}
      {step > 0 && (
        <div className="mt-6 flex justify-between">
          <button data-testid="mate-back-btn" onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="px-5 py-2.5 rounded-full border border-black/15 bg-white text-sm font-bold inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < 4 ? (
            <button data-testid="mate-next-btn" onClick={next} className="btn-primary inline-flex items-center gap-1">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="mate-submit-btn" onClick={submit} disabled={busy} className="btn-primary inline-flex items-center gap-1">
              {busy ? "Submitting…" : "Submit application"} <Sparkles className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
